import { useEffect, useRef, useState } from 'react'
import './AdBuilder.css'

// Live in-browser preview for an ad-builder config. Mirrors the parse
// logic in the worker's renderAdBuilderHtml (NonLinear → Companion →
// Linear ClickThrough) so what the admin sees matches the served page.
//
// Reuses the standalone app's VAST parsing model rather than the
// sponsor-manager's `fetchAndParseVast` helper — that helper looks for
// <MediaFile> (video), but our 970×250 case is a NonLinear banner
// image.

function parseVastBanner(xml) {
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  const text = node => (node && node.textContent ? node.textContent.trim() : '')
  let img = '', clk = ''
  const impressions = []
  for (const i of doc.getElementsByTagName('Impression')) {
    const t = text(i); if (t) impressions.push(t)
  }
  for (const nl of doc.getElementsByTagName('NonLinear')) {
    const sr = nl.getElementsByTagName('StaticResource')
    if (sr.length && text(sr[0])) img = text(sr[0])
    const ct = nl.getElementsByTagName('NonLinearClickThrough')
    if (ct.length && text(ct[0])) clk = text(ct[0])
    if (img) break
  }
  if (!img) {
    for (const comp of doc.getElementsByTagName('Companion')) {
      const sr = comp.getElementsByTagName('StaticResource')
      if (sr.length && text(sr[0])) { img = text(sr[0]); break }
    }
  }
  if (!clk) {
    const lcts = doc.getElementsByTagName('ClickThrough')
    if (lcts.length) clk = text(lcts[0])
  }
  return { img, click: clk, impressions }
}

export default function AdBuilderPreview({ config }) {
  const [parsed, setParsed] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const qrRef = useRef(null)
  const innerRef = useRef(null)
  const stageRef = useRef(null)

  // Scale the 1220×250 inner stage to fit whatever width the preview
  // panel happens to have.
  useEffect(() => {
    function fit() {
      if (!stageRef.current || !innerRef.current) return
      const w = stageRef.current.clientWidth
      const scale = Math.min(1, w / 1220)
      innerRef.current.style.transform = `scale(${scale})`
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [config.mode])

  const { mode, zone_id, oid, click_target } = config
  const cacheBust = useRef(Date.now()).current

  useEffect(() => {
    if (mode !== 'vast' || !zone_id) { setParsed(null); setErr(''); return }
    setLoading(true)
    setErr('')
    const url = `https://ads.tenband.tv/ad.php?do=vast&zid=${encodeURIComponent(zone_id)}` +
      `&oid=${encodeURIComponent(oid || '28740')}&wd=-1&ht=-1&vastver=3&cb=${Date.now()}`
    fetch(url)
      .then(r => r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(xml => {
        const p = parseVastBanner(xml)
        if (!p.img) throw new Error('No banner image in VAST response')
        setParsed(p)
      })
      .catch(e => { setErr(e.message); setParsed(null) })
      .finally(() => setLoading(false))
  }, [mode, zone_id, oid])

  // Lazy-load qrcodejs (same lib the rendered page uses) and render
  // a fresh QR whenever the parsed click URL changes.
  useEffect(() => {
    if (!parsed || !parsed.img) return
    const target = parsed.click || parsed.img
    function draw() {
      if (!qrRef.current || !window.QRCode) return
      qrRef.current.innerHTML = ''
      // eslint-disable-next-line no-new
      new window.QRCode(qrRef.current, { text: target, width: 240, height: 240,
        correctLevel: window.QRCode.CorrectLevel.M })
    }
    if (window.QRCode) { draw(); return }
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
    s.onload = draw
    document.head.appendChild(s)
  }, [parsed])

  const jsIframeUrl = mode === 'js' && zone_id
    ? `https://ads.tenband.tv/ad.php?do=js&zid=${encodeURIComponent(zone_id)}` +
      `&oid=${encodeURIComponent(oid || '28740')}&wd=-1&ht=-1` +
      `&target=${encodeURIComponent(click_target || '_blank')}&cb=${cacheBust}`
    : null

  return (
    <div className="ad-builder-preview-card">
      <h3>Preview</h3>
      <div className="ad-builder-preview-stage" ref={stageRef} style={{ background: config.bg_color || '#000' }}>
        <div className="ad-builder-preview-inner" ref={innerRef}>
          <div className="ad-builder-preview-ad">
            {mode === 'js' && jsIframeUrl && (
              <iframe title="ad preview" src={`data:text/html;charset=utf-8,${encodeURIComponent(
                `<!DOCTYPE html><html><head><base target="${config.click_target || '_blank'}">` +
                `<style>html,body{margin:0;padding:0;background:transparent;overflow:hidden}</style>` +
                `</head><body><script src="${jsIframeUrl}"><\/script></body></html>`
              )}`} />
            )}
            {mode === 'vast' && parsed?.img && (
              <img alt="" src={parsed.img} />
            )}
            {mode === 'vast' && !parsed && !loading && (
              <div style={{ color: '#666', fontSize: 12, padding: 12, textAlign: 'center' }}>
                {err
                  ? `Preview unavailable: ${err}${config.fallback_image_url ? ' (render will use the fallback image)' : ''}`
                  : (zone_id ? 'Click Save to fetch a VAST preview' : 'Pick a zone')}
              </div>
            )}
            {mode === 'vast' && loading && (
              <div style={{ color: '#888', fontSize: 12 }}>Fetching VAST…</div>
            )}
          </div>
          <div className="ad-builder-preview-qr" ref={qrRef}>
            {mode === 'js' && (
              <div style={{ color: '#666', fontSize: 11, padding: 8, textAlign: 'center' }}>
                QR generated at runtime from the iframe&apos;s click URL
              </div>
            )}
          </div>
        </div>
      </div>
      {parsed && (
        <p className="muted" style={{ fontSize: '0.72rem', marginTop: 8 }}>
          Beacons fired on render: {parsed.impressions.length} · click: {parsed.click || '(none in VAST)'}
        </p>
      )}
    </div>
  )
}
